import React, { useEffect, useState } from 'react'
import { Redirect, Switch, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'

import Header from 'components/Header/Header'
import Footer from 'components/Footer'
import UserRoleModal from 'components/Modal/UserRoleModal/UserRoleModal'
import TrialExtensionModal from 'components/Modal/TrialExtensionModal/TrialExtensionModal'
import OnboardingModal from 'components/Modal/OnboardingModal/OnboardingModal'
import useAnalyticsMonitor from 'components/useAnalyticsMonitor'
import { AssetManagerProvider } from 'components/AssetManager/AssetManagerContext'
import { MODALS } from 'components/ModalManager/constants'

import OrganizationInitializer from 'initializers/OrganizationInitializer'
import UserInitializer from 'initializers/UserInitializer'

import { API__PRODUCTS, API__USER } from 'api'

import {
    isEducation,
    isTrialSubscription,
    isSubscriptionActivated,
    getConstraintLimit,
    isConstraintUnlimited,
} from 'common/utils/permissions'
import AnalyticsMonitor from 'common/services/AnalyticsMonitor'
import { FEATURE_TYPES } from 'common/constants/subscription'

import AccountSettings from 'pages/AccountSettings/AccountSettings.page'
import BillingAndPayments from 'pages/BillingAndPayments/BillingAndPayments.page'
import MyProjects from 'pages/MyProjects/MyProjects.page'
import ProjectAnalytics from 'pages/ProjectAnalytics/ProjectAnalytics.page'
import Editor from 'pages/Editor/Editor.page'
import QuestionBank from 'pages/QuestionBank/QuestionBank.page'
import PublicPageStatistic from 'pages/PublicPageStatistic/PublicPageStatistic.page'
import MyPurchases from 'pages/MyPurchases/MyPurchases.page'

import ProtectedRoute from 'routes/ProtectedRoute'

import useUserSubscriptions from 'hooks/useUserSubscriptions'
import useModalManager from 'hooks/useModalManager'

import { ORGANIZATIONS__REFRESH_SELECTED_SUBSCRIPTION, USER__SET_DETAILS, USER__SET_SUBSCRIPTIONS } from 'store/actions'

import AdminPanel from './components/AdminPanel/AdminPanel'

const MainLayout = () => {
    useAnalyticsMonitor()
    const location = useLocation()
    const dispatch = useDispatch()
    const { user_details, organizations } = useSelector(state => state)
    const { constraints } = useUserSubscriptions()
    const { openModal } = useModalManager()

    const [isOpenedRoleModal, setIsOpenedRoleModal] = useState(user_details && !user_details.roleInProjectDto)
    const [isOpenedTrialModal, setIsOpenedTrialModal] = useState(false)
    const [isOpenedOnboardingModal, setIsOpenedOnboardingModal] = useState(false)

    useEffect(() => {
        setIsOpenedRoleModal(user_details && !user_details.roleInProjectDto)
    }, [user_details])

    useEffect(() => {
        if (!constraints) return
        const reviewCountConstraint = constraints[FEATURE_TYPES.REVIEWS_COUNT]
        if (!reviewCountConstraint || isConstraintUnlimited(reviewCountConstraint)) return
        if (reviewCountConstraint.spent >= getConstraintLimit(reviewCountConstraint)) {
            openModal(MODALS.VIEWS_LIMITS_MODAL)
        }
    }, [constraints])

    const onSelectUserRole = async data => {
        const { selectedOrganizationId } = organizations
        const selectedRole = data.role
        const updatedUser = await API__USER.SET_USER_ROLE(user_details.id, selectedRole.id)
        dispatch(USER__SET_DETAILS(updatedUser))

        if (!isSubscriptionActivated() && isTrialSubscription()) {
            if (isEducation()) await API__PRODUCTS.ACTIVATE_EDUCATION_FREE(selectedOrganizationId)
            else await API__PRODUCTS.ACTIVATE_TRIAL(selectedOrganizationId)

            AnalyticsMonitor.track('User role chosen', { role: selectedRole.id })
            await dispatch(ORGANIZATIONS__REFRESH_SELECTED_SUBSCRIPTION())
            await dispatch(USER__SET_SUBSCRIPTIONS(await API__USER.GET_SUBSCRIPTIONS(user_details.id)))

            if (isEducation()) setIsOpenedOnboardingModal(true)
            else setIsOpenedTrialModal(true)
        }
    }

    const isShowFooterAndHeader = !location.pathname.includes('/editor')

    return (
        <UserInitializer userId={user_details.id}>
            <OrganizationInitializer userId={user_details.id}>
                <AssetManagerProvider>
                    <div className={classNames('main', { 'is-editor-main': !isShowFooterAndHeader })}>
                        {isShowFooterAndHeader && <Header />}
                        <main>
                            <Switch>
                                <ProtectedRoute path="/editor/:projectId?" component={Editor} />
                                <ProtectedRoute exact path="/settings" component={AccountSettings} />
                                <ProtectedRoute exact path="/billing-and-payments" component={BillingAndPayments} />
                                <ProtectedRoute exact path="/my-projects" component={MyProjects} />
                                <ProtectedRoute path="/my-projects/:id/analytics" component={ProjectAnalytics} />
                                <ProtectedRoute path="/question-bank" component={QuestionBank} />
                                <ProtectedRoute path="/public-page-statistic" component={PublicPageStatistic} />
                                <ProtectedRoute path="/my-purchases" component={MyPurchases} />

                                <Redirect from="*" to="/my-projects" />
                            </Switch>
                        </main>
                        {isShowFooterAndHeader && <Footer />}
                        {isOpenedRoleModal && (
                            <UserRoleModal
                                isEditMode={false}
                                onSelect={data => onSelectUserRole(data)}
                                onClose={() => setIsOpenedRoleModal(false)}
                            />
                        )}
                        {isOpenedOnboardingModal && (
                            <OnboardingModal onClose={() => setIsOpenedOnboardingModal(false)} />
                        )}
                        {isOpenedTrialModal && <TrialExtensionModal onClose={() => setIsOpenedTrialModal(false)} />}
                    </div>
                    {user_details.adminAuth && <AdminPanel />}
                </AssetManagerProvider>
            </OrganizationInitializer>
        </UserInitializer>
    )
}

export default MainLayout
